/** Remove `$`-prefixed runtime fields while preserving snapshot structure. */
export function stripRuntimeState<T>(value: T): T {
  if (Array.isArray(value)) {
    let result: unknown[] | undefined
    for (let index = 0; index < value.length; index += 1) {
      const child: unknown = value[index]
      const persistent = stripRuntimeState(child)
      if (Object.is(child, persistent)) continue
      result ??= value.slice()
      result[index] = persistent
    }
    return (result ?? value) as T
  }

  if (!isPlainRecord(value)) return value

  let result: Record<string, unknown> | undefined
  for (const [key, child] of Object.entries(value)) {
    if (key.startsWith('$')) {
      result ??= { ...value }
      delete result[key]
      continue
    }
    const persistent = stripRuntimeState(child)
    if (Object.is(child, persistent)) continue
    result ??= { ...value }
    result[key] = persistent
  }
  return (result ?? value) as T
}

/** Test-harness guard for every externally visible document. */
export function assertNoRuntimeState(value: unknown): void {
  const runtimePath = findRuntimePath(value, '')
  if (runtimePath) {
    throw new Error(
      `Runtime state escaped persistence boundary at ${runtimePath}`,
    )
  }
}

function findRuntimePath(value: unknown, parentPath: string): string | null {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const path = findRuntimePath(item, `${parentPath}[${index}]`)
      if (path) return path
    }
    return null
  }

  if (!isPlainRecord(value)) return null
  for (const [key, child] of Object.entries(value)) {
    const path = parentPath ? `${parentPath}.${key}` : key
    if (key.startsWith('$')) return path
    const nested = findRuntimePath(child, path)
    if (nested) return nested
  }
  return null
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) return false
  const prototype = Reflect.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
